import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const getHESchoolDetails = ({ schoolId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/he-school/${schoolId}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const searchSchools = ({
  search = '',
  version = 'v1',
  limit,
  params = {},
}) => {
  const searchToApply = params?.search ?? search;
  const limitToApply = params?.limit ?? limit;
  return client
    .request({
      url: `${version}/higher-ed/he-school/`,
      method: RequestMethod.GET,
      params: { search: searchToApply, limit: limitToApply },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const getHeSchoolList = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/higher-ed/he-school/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const searchHESchools = ({
  searchTerm = '',
  params = {},
  version = 'v1',
  limit,
}) => {
  const searchToApply = params?.search ?? searchTerm;
  const limitToApply = params?.limit ?? limit;
  return client
    .request({
      url: `${version}/higher-ed/school-search/multi-search/college/`,
      method: RequestMethod.GET,
      params: { search: searchToApply, limit: limitToApply, ...params },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const searchSurveys = props => {
  const { version = 'v1', params = {} } = props;
  const { search = '' } = params;
  return client
    .request({
      url: `${version}/common/autocomplete/`,
      method: RequestMethod.GET,
      params: {
        term: search,
        app_label: 'alumni',
        fields: 'survey__name',
        model_name: 'AlumSurvey',
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);
};

export const searchMajors = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/school-search/multi-search/program`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get higher education college comparison list
 *
 * @param {Number} params.student - studentId
 * @param {String} version
 */
export const getHeCollegeComparisonList = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/he-school-comparison/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get higher education college details
 *
 * @param {Number} collegeId
 * @param {Number} params.student - studentId
 * @param {String} version
 */
export const getHeCollegeDetails = ({ collegeId, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/higher-ed/he-school-comparison/${collegeId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHeSchools = ({ studentId, params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/higher-ed/school-search/schools/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAllHeSchools = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/higher-ed/school-search/schools/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

// Small/efficient endpoint to return list of HESchools
export const getCachedHESchoolList = ({ params = {}, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/he-schools/cached/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getHESchoolEFC = ({ schoolId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/he-school/${schoolId}/efc/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFavoritedCollegeList = ({
  params,
  studentId,
  version = 'v1',
}) =>
  client
    .request({
      url: `${version}/higher-ed/favorites-list/${studentId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDegreeLevels = ({ version = 'v1' }) =>
  client
    .request({
      url: `${version}/majors/degree-levels/cached/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createHESchool = ({ version = 'v1', payload }) =>
  client
    .request({
      url: `${version}/higher-ed/he-school/`,
      method: RequestMethod.POST,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unauthenticatedHESchoolSearch = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `${version}/higher-ed/he-school/unauthenticated-search/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolLists = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/school-lists/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const favoriteSchoolMajor = ({ programUuid, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/favorite-school-major/${programUuid}/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unfavoriteSchoolMajor = ({ programUuid, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/favorite-school-major/${programUuid}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const favoriteSchool = ({ schoolUuid, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/favorite-school/${schoolUuid}/`,
      method: RequestMethod.POST,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateFavoriteSchool = ({
  schoolUuid,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/higher-ed/favorite-school/${schoolUuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const unfavoriteSchool = ({ schoolUuid, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/favorite-school/${schoolUuid}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getFavoriteReasons = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/favorite-reasons/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSchoolList = ({ listUuid, payload, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/higher-ed/school-list/${listUuid}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);
